import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { IPaginatedResponse } from '@getgreenline/homi-shared'
import { InventoryLogAPI } from '@getgreenline/inventory'
import {
  IInventoryLog,
  IInventoryLogFilter,
} from '@getgreenline/inventory/build/models/inventoryLog'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { AppMainView } from '../../../../components/AppView'
import AppHeader from '../../../../components/AppView/Header'
import { InventoryLogsSearchFilters } from '../../../../components/InventoryLogs/InventoryLogsSearchFilters'
import { InventoryLogsTable } from '../../../../components/InventoryLogs/InventoryLogsTable'
import { ExportInventoryLogsButton } from '../../../../components/InventoryLogs/ExportInventoryLogsButton'
import Helmet from 'react-helmet'
import { message } from 'antd'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { CustomTablePagination } from '../../../../components/CustomTablePagination'
import { dayjs } from '../../../../utilities/dayjs'

interface Props {
  currentCompanyStore: CurrentCompanyStore
  currentUserStore: CurrentUserStore
}

interface State {
  logs: IPaginatedResponse<IInventoryLog>
  isLoading: boolean
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class InventoryLogs extends React.Component<Props, State> {
  inventoryLogFilter: IInventoryLogFilter = {
    limit: 20,
    offset: 0,
    productIds: [],
    categoryIds: [],
    employeeIds: [],
    notesQuery: '',
    startDate: dayjs().subtract(1, 'month').startOf('month').toDate(),
    endDate: dayjs().endOf('day').toDate(),
  }
  constructor(props: Props) {
    super(props)
    this.state = {
      logs: {
        data: [],
        pagination: {
          limit: this.inventoryLogFilter.limit || 20,
          offset: this.inventoryLogFilter.offset || 0,
        },
      },
      isLoading: false,
    }
  }

  componentDidMount() {
    // Load employees
    this.props.currentCompanyStore.getEntityUsers()

    // Load locations
    this.props.currentCompanyStore.getLocations()

    // Load logs
    this.getInventoryLogs()
  }

  getInventoryLogs = async () => {
    const company = this.props.currentCompanyStore!.company!
    this.setState({ isLoading: true })
    try {
      const paginatedLogs = await InventoryLogAPI.getInventoryLogs(
        company.id,
        this.inventoryLogFilter,
      )
      this.setState({ logs: paginatedLogs })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading inventory logs')
    } finally {
      this.setState({ isLoading: false })
    }
  }

  handleLogsPerPageChange = (perPage: number) => {
    this.inventoryLogFilter.limit = perPage
    this.inventoryLogFilter.offset = 0

    this.getInventoryLogs()
  }

  handlePaginationClick = (offset: number) => {
    this.inventoryLogFilter.offset = offset
    this.getInventoryLogs()
  }

  render() {
    const { logs, isLoading } = this.state
    const { locationId, employeeIds, type, limit, offset } = this.inventoryLogFilter
    const companyId = this.props.currentCompanyStore.company!.id

    return (
      <div className='d-flex flex-column h-100'>
        <Helmet>
          <title>Reports | Inventory Logs</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader title='Inventory logs'>
          <ExportInventoryLogsButton
            locationId={locationId}
            employeeIds={employeeIds || []}
            logType={type}
            productIds={this.inventoryLogFilter.productIds || []}
          />
        </AppHeader>

        <AppMainView className='background-grey intercom-pb-2'>
          <InventoryLogsSearchFilters
            currentCompanyStore={this.props.currentCompanyStore}
            currentUserStore={this.props.currentUserStore}
            inventoryLogFilter={this.inventoryLogFilter}
            onLogsPerPageChange={this.handleLogsPerPageChange}
            isLoading={isLoading}
            hideFilters={['export']}
          />

          <CustomTablePagination
            onClick={this.handlePaginationClick}
            data={this.state.logs.data}
            showFirstPage
            hidePerPageOptions
            paginationPosition={'both'}
            pagination={{ limit: limit || 20, offset: offset || 0 }}
          >
            <InventoryLogsTable
              companyId={companyId}
              logs={logs.data}
              showProductColumn
              onChange={this.getInventoryLogs}
              isLoading={isLoading}
            />
          </CustomTablePagination>
        </AppMainView>
      </div>
    )
  }
}
